import React, {
  createContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useContext,
  useRef,
} from 'react';

import { CPLAT_API_ROUTES, usePost } from '@/api';
import { useAppSelector } from '@/redux/store';
import { getActivePeriod } from '../V3/Statistics/_utils';
import { formatDateToKST } from '@/utils/parser';
import {
  CalendarDaily,
  MonthlySales,
  SalesInfo,
  SettleInfo,
  SalesMarket,
  SalesProduct,
  SalesStore,
  SalesInfoInitial,
  SettleInfoInitial,
  MonthlySalesInitial,
  Market,
} from '@/types/statistics';
import { CplatApiResponse } from '@/types/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import {
  getMembershipStatisticsCalendarMock,
  getMembershipStatisticsDailyMock,
  getMembershipStatisticsPeriodDetailMock,
  membershipStatisticsMock,
} from './membership-statistics-mock';
import {
  createStatisticsDailyCacheValue,
  getShouldRefreshStatisticsDailyCache,
  getStatisticsDailyCacheKey,
  type StatisticsDailyCacheValue,
} from './statistics-daily-cache';
import {
  createStatisticsCalendarMonthCacheValue,
  createStatisticsPeriodDetailMonthCacheValue,
  getStatisticsMonthCacheKey,
  type StatisticsCalendarMonthCacheValue,
  type StatisticsPeriodDetailMonthCacheValue,
} from './statistics-monthly-cache';
interface StatisticsContextType {
  salesInfo: SalesInfo;
  settleInfo: SettleInfo;
  calendarDailyList: Array<CalendarDaily>;
  monthlySalesList: Array<MonthlySales>;
  salesMarketList: Array<SalesMarket>;
  salesProductList: Array<SalesProduct>;
  salesStoreList: Array<SalesStore>;
  totalSalesAmount: number;
  activedMonthlySales: MonthlySales;
  setActivedMonthlySales: React.Dispatch<React.SetStateAction<MonthlySales>>;
  isLoading: boolean;
  selectedDate: string;
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>;
  dailyMarketList: Market[];
  dailySalesInfo: SalesInfo;
  dailySettleInfo: SettleInfo;
  isDailyLoading: boolean;
  isRegisteredMarket: boolean;
}

export const StatisticsContext = createContext<StatisticsContextType | undefined>(undefined);

interface StatisticsProviderProps {
  children: React.ReactNode;
}

const getInitialPreviewActivedMonthlySales = () =>
  membershipStatisticsMock.monthlySalesList[membershipStatisticsMock.monthlySalesList.length - 1] ??
  MonthlySalesInitial;

export const StatisticsProvider = ({ children }: StatisticsProviderProps) => {
  // 상태 초기값 지정
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();
  const [salesInfo, setSalesInfo] = useState<SalesInfo>(SalesInfoInitial);
  const [settleInfo, setSettleInfo] = useState<SettleInfo>(SettleInfoInitial);
  const [calendarDailyList, setCalendarDailyList] = useState<Array<CalendarDaily>>([]);
  const [monthlySalesList, setMonthlySalesList] = useState<Array<MonthlySales>>([]);
  const [salesMarketList, setSalesMarketList] = useState<Array<SalesMarket>>([]);
  const [salesProductList, setSalesProductList] = useState<Array<SalesProduct>>([]);
  const [salesStoreList, setSalesStoreList] = useState<Array<SalesStore>>([]);
  const [totalSalesAmount, setTotalSalesAmount] = useState<number>(0);
  const [activedMonthlySales, setActivedMonthlySales] = useState<MonthlySales>(MonthlySalesInitial);
  const [previewActivedMonthlySales, setPreviewActivedMonthlySales] = useState<MonthlySales>(
    getInitialPreviewActivedMonthlySales,
  );
  const [previewSelectedDate, setPreviewSelectedDate] = useState<string>(
    membershipStatisticsMock.selectedDate,
  );
  const [isLoading, setIsLoading] = useState(false);

  const [dailyMarketList, setDailyMarketList] = useState<Market[]>([]);
  const [dailySalesInfo, setDailySalesInfo] = useState<SalesInfo>(SalesInfoInitial);
  const [dailySettleInfo, setDailySettleInfo] = useState<SettleInfo>(SettleInfoInitial);
  const [isDailyLoading, setIsDailyLoading] = useState<boolean>(false);
  const dailyStatisticsCacheRef = useRef<Record<string, StatisticsDailyCacheValue>>({});
  const monthlyCalendarCacheRef = useRef<Record<string, StatisticsCalendarMonthCacheValue>>({});
  const monthlyPeriodDetailCacheRef = useRef<Record<string, StatisticsPeriodDetailMonthCacheValue>>(
    {},
  );
  // 오늘 날짜를 string으로 변환
  const mountedTodayStrRef = useRef<string | null>(null);
  if (mountedTodayStrRef.current === null) {
    mountedTodayStrRef.current = formatDateToKST(new Date()); // 예: '2025-06-15'
  }
  const todayStr = mountedTodayStrRef.current;

  // 선택된 오늘의 날짜
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);

  // API hooks
  const { mutate: getCalendar } = usePost(CPLAT_API_ROUTES.getCalendar);
  const { mutate: getPeriodReport } = usePost(CPLAT_API_ROUTES.getPeriodReport);
  const { mutate: getPeriodDetail } = usePost(CPLAT_API_ROUTES.getPeriodDetail);

  const applyCalendarMonthStatistics = useCallback(
    (monthlyStatistics: StatisticsCalendarMonthCacheValue) => {
      setSalesInfo(monthlyStatistics.salesInfo);
      setSettleInfo(monthlyStatistics.settleInfo);
      setCalendarDailyList(monthlyStatistics.calendarDailyList);
    },
    [],
  );

  const applyPeriodDetailMonthStatistics = useCallback(
    (monthlyStatistics: StatisticsPeriodDetailMonthCacheValue) => {
      setSalesMarketList(monthlyStatistics.salesMarketList);
      setSalesProductList(monthlyStatistics.salesProductList);
      setSalesStoreList(monthlyStatistics.salesStoreList);
    },
    [],
  );

  // 최초 로딩 시 월별 리포트 및 캘린더 데이터 불러오기
  useEffect(() => {
    if (isPreviewMode) return;
    if (!user?.CplatToken) return;
    setIsLoading(true);

    // 1. 월별 리포트
    const { dateFrom, dateTo } = getActivePeriod(todayStr, 7, true);
    getPeriodReport(
      {
        cplatToken: user.CplatToken,
        dateFrom,
        dateTo,
      },
      {
        onSuccess(res) {
          const { data, code } = res.data;
          if (code === '200' && data.length > 0) {
            const { TotalSalesAmount, MonthlySalesList } = data[0].SalesInfo;
            setTotalSalesAmount(TotalSalesAmount);
            setMonthlySalesList(MonthlySalesList);
            setActivedMonthlySales(
              MonthlySalesList[MonthlySalesList.length - 1] ?? MonthlySalesInitial,
            );
          }
        },

        onSettled() {
          setIsLoading(false);
        },
      },
    );

    // 2. 캘린더 데이터
    const todayMonthCacheKey = getStatisticsMonthCacheKey(todayStr);
    const { dateFrom: calFrom, dateTo: calTo } = getActivePeriod(todayStr, 1, false);
    getCalendar(
      {
        cplatToken: user.CplatToken,
        dateFrom: calFrom,
        dateTo: calTo,
      },
      {
        onSuccess(res) {
          const data = res.data.data?.[0];
          const nextCalendarStatistics = createStatisticsCalendarMonthCacheValue(data);

          applyCalendarMonthStatistics(nextCalendarStatistics);
          monthlyCalendarCacheRef.current[todayMonthCacheKey] = nextCalendarStatistics;
        },
        onSettled() {
          setIsLoading(false);
        },
      },
    );
  }, [
    applyCalendarMonthStatistics,
    getCalendar,
    getPeriodReport,
    isPreviewMode,
    todayStr,
    user?.CplatToken,
  ]);

  useEffect(() => {
    if (isPreviewMode) return;
    if (!user?.CplatToken || !activedMonthlySales?.Month) return;

    // 1) 월별 상세 데이터 호출 (기존)
    const activeMonthCacheKey = getStatisticsMonthCacheKey(activedMonthlySales.Month);
    const cachedPeriodDetailStatistics = monthlyPeriodDetailCacheRef.current[activeMonthCacheKey];
    const cachedCalendarStatistics = monthlyCalendarCacheRef.current[activeMonthCacheKey];

    if (cachedPeriodDetailStatistics && cachedCalendarStatistics) {
      applyPeriodDetailMonthStatistics(cachedPeriodDetailStatistics);
      applyCalendarMonthStatistics(cachedCalendarStatistics);
      setIsLoading(false);
      return;
    }

    if (cachedPeriodDetailStatistics) {
      applyPeriodDetailMonthStatistics(cachedPeriodDetailStatistics);
    }

    if (cachedCalendarStatistics) {
      applyCalendarMonthStatistics(cachedCalendarStatistics);
    }

    setIsLoading(true);

    const [year, month] = activeMonthCacheKey.split('-').map(Number);
    const activedDate = new Date(year, month - 1, 1);
    const activedDateStr = formatDateToKST(activedDate);

    const isCurrentMonth = new Date().getFullYear() === year && new Date().getMonth() + 1 === month;
    const { dateFrom, dateTo } = getActivePeriod(activedDateStr, 1, isCurrentMonth);

    if (!cachedPeriodDetailStatistics) {
      getPeriodDetail(
        {
          cplatToken: user.CplatToken,
          dateFrom,
          dateTo,
        },
        {
          onSuccess(res) {
            const { data, code } = res.data;
            const nextPeriodDetailStatistics = createStatisticsPeriodDetailMonthCacheValue(
              code === '200' && Array.isArray(data) ? data[0] : undefined,
            );

            applyPeriodDetailMonthStatistics(nextPeriodDetailStatistics);
            monthlyPeriodDetailCacheRef.current[activeMonthCacheKey] = nextPeriodDetailStatistics;
          },
          onSettled() {
            setIsLoading(false);
          },
        },
      );
    }

    // 2) 매출/정산 데이터 갱신 API 호출 추가
    // getCalendar API 호출 (기간은 activedMonthlySales 월의 1일 ~ 말일)
    const dateFromCalendar = new Date(year, month - 1, 1);
    const dateToCalendar = new Date(year, month, 0); // 해당 월 마지막 날

    if (!cachedCalendarStatistics) {
      getCalendar(
        {
          cplatToken: user.CplatToken,
          dateFrom: formatDateToKST(dateFromCalendar),
          dateTo: formatDateToKST(dateToCalendar),
        },
        {
          onSuccess(res) {
            const data = res.data.data?.[0];
            const nextCalendarStatistics = createStatisticsCalendarMonthCacheValue(data);

            applyCalendarMonthStatistics(nextCalendarStatistics);
            monthlyCalendarCacheRef.current[activeMonthCacheKey] = nextCalendarStatistics;
          },
          onSettled() {
            setIsLoading(false);
          },
        },
      );
    }
  }, [
    activedMonthlySales?.Month,
    applyCalendarMonthStatistics,
    applyPeriodDetailMonthStatistics,
    getCalendar,
    getPeriodDetail,
    isPreviewMode,
    user?.CplatToken,
  ]);

  const { mutate: getCalendarDaily } = usePost(CPLAT_API_ROUTES.getCalendarDaily);

  const applyDailyStatistics = useCallback((dailyStatistics: StatisticsDailyCacheValue) => {
    setDailyMarketList(dailyStatistics.dailyMarketList);
    setDailySalesInfo(dailyStatistics.dailySalesInfo);
    setDailySettleInfo(dailyStatistics.dailySettleInfo);
  }, []);

  // 날짜가 바뀔 때마다 상세 데이터 fetch
  useEffect(() => {
    if (isPreviewMode) return;
    if (!user?.CplatToken || !selectedDate) return;

    const selectedDateCacheKey = getStatisticsDailyCacheKey(selectedDate);
    const shouldRefreshDailyStatistics = getShouldRefreshStatisticsDailyCache(
      selectedDate,
      formatDateToKST(new Date()),
    );
    const cachedDailyStatistics = dailyStatisticsCacheRef.current[selectedDateCacheKey];

    if (!shouldRefreshDailyStatistics && cachedDailyStatistics) {
      applyDailyStatistics(cachedDailyStatistics);
      setIsDailyLoading(false);
      return;
    }

    setIsDailyLoading(true);
    getCalendarDaily(
      { cplatToken: user.CplatToken, dateFrom: selectedDate, dateTo: selectedDate },
      {
        onSuccess(res) {
          const response = res?.data;
          if (!response) return;

          const { data, code } = response;
          const nextDailyStatistics = createStatisticsDailyCacheValue(
            code === '200' && Array.isArray(data) ? data[0] : undefined,
          );

          applyDailyStatistics(nextDailyStatistics);
          dailyStatisticsCacheRef.current[selectedDateCacheKey] = nextDailyStatistics;
        },
        onSettled() {
          setIsDailyLoading(false);
        },
      },
    );
  }, [applyDailyStatistics, getCalendarDaily, isPreviewMode, selectedDate, user?.CplatToken]);

  const { mutate: getRegisteredMarketInfo } = usePost(CPLAT_API_ROUTES.getRegisteredMarketInfo);
  const [isRegisteredMarket, setIsRegisteredMarket] = useState<boolean>(true);

  const handleGetRegisteredMarketInfo = useCallback(() => {
    if (isPreviewMode) {
      setIsRegisteredMarket(true);
      return;
    }

    getRegisteredMarketInfo(
      {
        cplatToken: user.CplatToken,
      },
      {
        onSuccess(res) {
          const { code } = res.data as CplatApiResponse<unknown[]>;
          if (code === '200') {
            setIsRegisteredMarket(true);
          } else {
            setIsRegisteredMarket(false);
          }
        },
      },
    );
  }, [getRegisteredMarketInfo, isPreviewMode, user.CplatToken]);

  useEffect(() => {
    handleGetRegisteredMarketInfo();
  }, [handleGetRegisteredMarketInfo]);

  const previewCalendarDailyList = useMemo(
    () => getMembershipStatisticsCalendarMock(previewActivedMonthlySales),
    [previewActivedMonthlySales],
  );

  const previewDailyStatistics = useMemo(
    () => getMembershipStatisticsDailyMock(previewSelectedDate, previewCalendarDailyList),
    [previewCalendarDailyList, previewSelectedDate],
  );

  const previewPeriodDetailStatistics = useMemo(
    () => getMembershipStatisticsPeriodDetailMock(previewActivedMonthlySales),
    [previewActivedMonthlySales],
  );

  const value = useMemo(
    () => ({
      salesInfo: isPreviewMode ? membershipStatisticsMock.salesInfo : salesInfo,
      settleInfo: isPreviewMode ? membershipStatisticsMock.settleInfo : settleInfo,
      calendarDailyList: isPreviewMode ? previewCalendarDailyList : calendarDailyList,
      monthlySalesList: isPreviewMode
        ? membershipStatisticsMock.monthlySalesList
        : monthlySalesList,
      salesMarketList: isPreviewMode
        ? previewPeriodDetailStatistics.salesMarketList
        : salesMarketList,
      salesProductList: isPreviewMode
        ? previewPeriodDetailStatistics.salesProductList
        : salesProductList,
      salesStoreList: isPreviewMode ? previewPeriodDetailStatistics.salesStoreList : salesStoreList,
      totalSalesAmount: isPreviewMode ? previewActivedMonthlySales.SalesAmount : totalSalesAmount,
      activedMonthlySales: isPreviewMode ? previewActivedMonthlySales : activedMonthlySales,
      setActivedMonthlySales: isPreviewMode
        ? setPreviewActivedMonthlySales
        : setActivedMonthlySales,
      isLoading: isPreviewMode ? false : isLoading,
      selectedDate: isPreviewMode ? previewSelectedDate : selectedDate,
      setSelectedDate: isPreviewMode ? setPreviewSelectedDate : setSelectedDate,
      dailyMarketList: isPreviewMode ? previewDailyStatistics.dailyMarketList : dailyMarketList,
      dailySalesInfo: isPreviewMode ? previewDailyStatistics.dailySalesInfo : dailySalesInfo,
      dailySettleInfo: isPreviewMode ? previewDailyStatistics.dailySettleInfo : dailySettleInfo,
      isDailyLoading: isPreviewMode ? false : isDailyLoading,
      isRegisteredMarket: isPreviewMode ? true : isRegisteredMarket,
    }),
    [
      activedMonthlySales,
      calendarDailyList,
      dailyMarketList,
      dailySalesInfo,
      dailySettleInfo,
      isDailyLoading,
      isLoading,
      isPreviewMode,
      isRegisteredMarket,
      monthlySalesList,
      previewActivedMonthlySales,
      previewCalendarDailyList,
      previewDailyStatistics,
      previewPeriodDetailStatistics,
      previewSelectedDate,
      salesMarketList,
      salesProductList,
      salesStoreList,
      selectedDate,
      salesInfo,
      settleInfo,
      totalSalesAmount,
    ],
  );

  return <StatisticsContext.Provider value={value}>{children}</StatisticsContext.Provider>;
};

// 커스텀 훅 제공 (추천)
export const useStatistics = () => {
  const context = useContext(StatisticsContext);
  if (!context) throw new Error('useStatistics must be used within a StatisticsProvider');
  return context;
};
